/**
 * DocumentReviewListPage — lists all document reviews for the tenant.
 *
 * Gated behind the document_review feature flag.
 * Each row links to DocumentAnnotator so internal actors can upload externally-stamped PDFs.
 */
import { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, FileText, X, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DocumentAnnotator } from '@/components/document-review/DocumentAnnotator';
import { AuditTrail } from '@/components/document-review/AuditTrail';
import { FeatureGate } from '@/components/features/FeatureGate';
import { api } from '@/lib/api';

interface ReviewRow {
  id: string;
  status: string;
  mode: string;
  file_ref?: string;
  submittal_id?: string;
  client_action_id?: string | null;
  title?: string | null;
  project_id?: string | null;
  created_at?: string;
}

interface ActiveReview {
  id: string;
  status: string;
  pdfUrl: string;
  projectId?: string | null;
  externallyStampedPdfRef?: string | null;
  auditCertPdfRef?: string | null;
}

export function DocumentReviewListPage() {
  const [activeReview, setActiveReview] = useState<ActiveReview | null>(null);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [auditOpenId, setAuditOpenId] = useState<string | null>(null);
  // P10: cancelled reviews are noise on the everyday list. Hide by default,
  // surface a toggle so they can still be inspected when needed.
  const [showCancelled, setShowCancelled] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['document-reviews-list'],
    queryFn: () => api.documentReviews.list(),
  });

  const visibleReviews = (data?.reviews ?? []).filter(
    (r: ReviewRow) => showCancelled || r.status !== 'cancelled',
  );
  const hiddenCancelledCount = (data?.reviews ?? []).filter(
    (r: ReviewRow) => r.status === 'cancelled',
  ).length;

  const handleRowClick = useCallback(async (review: ReviewRow) => {
    if (loadingId) return;
    setLoadingId(review.id);
    try {
      // Fetch full review record (for externally_stamped_pdf_ref + audit_cert_pdf_ref)
      // in parallel with the PDF blob — both are needed to render the locked
      // banner on re-open of an already-externally-stamped review.
      const [pdfUrl, detail] = await Promise.all([
        api.documentReviews.downloadOriginalPdf(review.id),
        api.documentReviews.get(review.id).catch(() => null),
      ]);
      const r = detail?.review ?? {};
      setActiveReview({
        id: review.id,
        status: review.status,
        pdfUrl,
        // Project id is required by the Send-for-Review dialog to populate
        // scope-contact recipients. The list row carries it (joined in the
        // backend); fall back to the GET response if the row didn't have it.
        projectId: review.project_id ?? r.project_id ?? null,
        externallyStampedPdfRef: r.externally_stamped_pdf_ref ?? null,
        auditCertPdfRef: r.audit_cert_pdf_ref ?? null,
      });
    } catch {
      setActiveReview({ id: review.id, status: review.status, pdfUrl: '' });
    } finally {
      setLoadingId(null);
    }
  }, [loadingId]);

  const handleClose = useCallback(() => {
    if (activeReview?.pdfUrl) {
      URL.revokeObjectURL(activeReview.pdfUrl);
    }
    setActiveReview(null);
  }, [activeReview]);

  if (activeReview) {
    return (
      <div className="fixed inset-0 z-50 bg-background flex flex-col">
        <div className="flex items-center justify-between px-4 py-2 border-b bg-background shrink-0">
          <span className="text-sm font-medium">Document Review</span>
          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex-1 overflow-hidden">
          <DocumentAnnotator
            documentReviewId={activeReview.id}
            pdfUrl={activeReview.pdfUrl}
            mode="approve"
            reviewStatus={activeReview.status}
            projectId={activeReview.projectId ?? undefined}
            externallyStampedPdfRef={activeReview.externallyStampedPdfRef}
            auditCertPdfRef={activeReview.auditCertPdfRef}
            onClose={handleClose}
          />
        </div>
      </div>
    );
  }

  return (
    <DashboardLayout>
      <FeatureGate feature="document_review" fallback="message">
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-semibold">Document Reviews</h1>
            {hiddenCancelledCount > 0 && (
              <button
                type="button"
                className="text-xs text-muted-foreground hover:text-foreground underline"
                onClick={() => setShowCancelled((v) => !v)}
                data-testid="toggle-cancelled-reviews"
              >
                {showCancelled
                  ? `Hide cancelled (${hiddenCancelledCount})`
                  : `Show cancelled (${hiddenCancelledCount})`}
              </button>
            )}
          </div>

          {isLoading && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading…</span>
            </div>
          )}

          {!isLoading && visibleReviews.length === 0 && (
            <p className="text-sm text-muted-foreground">No document reviews found.</p>
          )}

          {visibleReviews.length > 0 && (
            <div className="rounded-md border divide-y">
              {visibleReviews.map((review: ReviewRow) => (
                <div
                  key={review.id}
                  className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 cursor-pointer transition-colors"
                  data-testid="document-review-row"
                  onClick={() => handleRowClick(review)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleRowClick(review); }}
                >
                  {loadingId === review.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-muted-foreground shrink-0" />
                  ) : (
                    <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    {review.title ? (
                      <>
                        <div className="text-sm font-medium truncate">{review.title}</div>
                        <div className="text-xs font-mono text-muted-foreground truncate">{review.id}</div>
                      </>
                    ) : (
                      <div className="text-sm font-mono text-muted-foreground truncate">{review.id}</div>
                    )}
                    {review.submittal_id && (
                      <div className="text-xs text-muted-foreground">Submittal {review.submittal_id}</div>
                    )}
                  </div>
                  <Badge
                    variant="outline"
                    className="text-xs shrink-0"
                  >
                    {review.status}
                  </Badge>
                  {review.created_at && (
                    <span className="text-xs text-muted-foreground shrink-0">
                      {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  )}
                  {/* F10: audit-trail quick-access. Click stops row open. */}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 shrink-0"
                    title="Show audit trail"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAuditOpenId(review.id);
                    }}
                  >
                    <History className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* F10: Audit trail slide-over panel */}
        {auditOpenId && (
          <div
            className="fixed inset-0 z-40 bg-black/30"
            onClick={() => setAuditOpenId(null)}
            data-testid="audit-trail-backdrop"
          >
            <aside
              className="absolute right-0 top-0 h-full w-full max-w-md bg-background border-l shadow-xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
              data-testid="audit-trail-drawer"
            >
              <div className="flex items-center justify-between px-4 py-2 border-b">
                <span className="text-sm font-medium">Audit Trail</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => setAuditOpenId(null)}
                  title="Close audit trail"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <AuditTrail reviewId={auditOpenId} className="flex-1 overflow-hidden" />
            </aside>
          </div>
        )}
      </FeatureGate>
    </DashboardLayout>
  );
}
